import React, { useState, useEffect } from 'react';
import { hexToTailwind } from '../../../utils/visualEditor/colorMapper';

/**
 * Color Editor
 * Edit text color and background color
 */
export default function ColorEditor({ element, onUpdate }) {
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  
  useEffect(() => {
    if (element?.styles) {
      setTextColor(rgbToHex(element.styles.color));
      setBackgroundColor(rgbToHex(element.styles.backgroundColor));
    }
  }, [element]);

  const handleTextColorChange = (color) => {
    setTextColor(color);
    
    // Get style path
    const stylePath = element?.element.getAttribute('data-style-path') || 
                     inferStylePath(element?.dataPath);
    
    if (stylePath) {
      // Convert hex to Tailwind class using comprehensive mapper
      const tailwindClass = hexToTailwind(color, 'text');
      console.log(`🎨 Color change: ${color} → ${tailwindClass}`);
      onUpdate(`${stylePath}.color`, tailwindClass);
    }
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    
    // Get style path
    const stylePath = element?.element.getAttribute('data-style-path') || 
                     inferStylePath(element?.dataPath);
    
    if (stylePath) {
      // Convert hex to Tailwind class using comprehensive mapper
      const tailwindClass = hexToTailwind(color, 'bg');
      console.log(`🎨 Background color change: ${color} → ${tailwindClass}`);
      onUpdate(`${stylePath}.backgroundColor`, tailwindClass);
    }
  };

  const inferStylePath = (dataPath) => {
    if (!dataPath) return null;
    const parts = dataPath.split('.');
    const lastPart = parts[parts.length - 1].replace(/\[\d+\]/, '');
    return `styles.${lastPart}`;
  };

  return (
    <div className="space-y-4">
      {/* Text Color */}
      {element?.editableProperties?.color && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Text Color
          </label>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="color"
              value={textColor}
              onChange={(e) => handleTextColorChange(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={textColor}
              onChange={(e) => handleTextColorChange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="#000000"
            />
          </div>
        </div>
      )}

      {/* Background Color */}
      {element?.editableProperties?.backgroundColor && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Background Color
          </label>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => handleBackgroundColorChange(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => handleBackgroundColorChange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="#ffffff"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Convert RGB string to HEX
 */
function rgbToHex(rgb) {
  if (!rgb) return '#000000';
  
  // Already hex
  if (rgb.startsWith('#')) return rgb;
  
  // Parse rgb(r, g, b) or rgba(r, g, b, a)
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return '#000000';
  
  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);
  
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

